/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable, inject } from '@angular/core';
import { Actions, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { AuthState } from '../state/auth.state';
import { BeginTokenRefresh } from '../state/auth.actions';
import { AuthenticatedUser } from '../state/auth.model';
import { LoggerService } from '../../../modules/shared/services/logger.service';

@Injectable()
export class AuthService {
    public user$: Observable<AuthenticatedUser> = inject(Store).select(AuthState.user);

    private isValidatingToken: boolean;
    private validatedToken: string;

    constructor(private store: Store, private actions$: Actions) {}

    public isAuthenticated() {
        let result = false;
        if (!this.isValidatingToken) {
            const token = this.validatedToken;
            result = (token && 0 < token.length) || false;
        }
        return result;
    }

    public isAuthorized$(authorizedRoles: string | string[]): Observable<boolean> {
        return this.isAdmin$().pipe(
            map((isAdmin) => {
                if (isAdmin) {
                    return isAdmin;
                }
                const user = this.store.selectSnapshot(AuthState.user);
                return this.checkRoles(user, authorizedRoles);
            }),
            take(1)
        );
    }

    isAuthorized(authorizedRoles: string | string[]): boolean {
        const user = this.store.selectSnapshot(AuthState.user);
        if (this.userIsAdmin(user)) {
            return true;
        }
        return this.checkRoles(user, authorizedRoles);
    }

    isAdmin$(): Observable<boolean> {
        return this.user$.pipe(map((user) => this.userIsAdmin(user)));
    }

    isAdmin(): boolean {
        const user = this.store.selectSnapshot(AuthState.user);
        return this.userIsAdmin(user);
    }

    public refreshToken() {
        this.store.dispatch(new BeginTokenRefresh());
    }

    private userIsAdmin(user: AuthenticatedUser): boolean {
        return user ? user.roles.findIndex((r) => r.toLowerCase() === 'admin') !== -1 : false;
    }

    private checkRoles(user: AuthenticatedUser, authorizedRoles: string | string[]): boolean {
        let authorized = false;
        if (!Array.isArray(authorizedRoles)) {
            authorizedRoles = [authorizedRoles];
        }
        if (this.isAuthenticated && user) {
            if (0 === authorizedRoles.length) {
                // no roles specified, so authorized
                authorized = true;
            }
            if (user.roles) {
                user.roles.forEach((role) => {
                    if (authorizedRoles.indexOf(role) !== -1) {
                        authorized = true;
                    }
                });
            }
        }
        return authorized;
    }
}
